import { compact } from '@art-suite/art-core-ts';
import { ApiPropertyProject, ApiUpdatePropertyProject } from '@kaiizen/kai-pro-api';
import { useNavigate } from 'react-router';

import { formatUsd, normalizeProjectType } from '../../Presenters';
import { useProjectTypes } from '../../State';
import { LAYOUT2 } from '../../Styles';
import { LabeledInput, LabeledSelect, PropsCard } from '../../Widgets';

const INPUT_WIDTH = LAYOUT2.spacing * 35 + 'px';

export type ProjectUpdateMutator = (updates: ApiUpdatePropertyProject) => void;

export const ProjectCard = ({ propertyProject, noLinkButton, editable, updateProject, buttonLabel, onButtonClick }: {
  propertyProject: ApiPropertyProject | undefined;
  noLinkButton?: boolean;
  editable?: boolean;
  updateProject?: ProjectUpdateMutator;
  buttonLabel?: string;
  onButtonClick?: () => void;
}) => {
  const navigate = useNavigate();
  const { getProjectTypeLabel, getProjectTypeOptions } = useProjectTypes();
  const selectedProjectType = propertyProject?.customerMetadata?.projectType
    ? normalizeProjectType(propertyProject.customerMetadata.projectType)
    : '';
  const projectTypeOptions = getProjectTypeOptions(selectedProjectType);

  if (editable && updateProject && propertyProject) {
    return (
      <PropsCard
        alignLabels='center'
        title='Project'
        buttonLabel={buttonLabel ?? (noLinkButton ? undefined : 'View Project')}
        onButtonClick={onButtonClick ?? (noLinkButton ? undefined : () => navigate(`/project/${propertyProject?.id}`))}
        items={[
          {
            label: 'Name',
            value: (
              <LabeledInput
                width={INPUT_WIDTH}
                size='medium'
                value={propertyProject.name || ''}
                onUpdate={value => updateProject({ name: value })}
                title='Project name'
              />
            ),
          },
          {
            label: 'Type',
            value: (
              <LabeledSelect
                width={INPUT_WIDTH}
                size='medium'
                value={selectedProjectType}
                items={projectTypeOptions}
                onUpdate={value => updateProject({ customerMetadata: { ...propertyProject.customerMetadata, projectType: value } })}
                title='Project type'
                placeholder='Select...'
              />
            ),
          },
          {
            label: 'Budget',
            value: (
              <LabeledInput
                width={INPUT_WIDTH}
                size='medium'
                type='number'
                unit='usd'
                value={propertyProject.customerMetadata?.budget ? String(propertyProject.customerMetadata.budget) : ''}
                onUpdate={value => updateProject({ customerMetadata: { ...propertyProject.customerMetadata, budget: value ? Number(value) : null } })}
                title='Project budget'
              />
            ),
          },
          {
            label: 'Description',
            value: (
              <LabeledInput
                width={INPUT_WIDTH}
                size='medium'
                multiline={true}
                value={propertyProject.description || ''}
                onUpdate={value => updateProject({ description: value })}
                title='Project description'
              />
            ),
          },
        ]}
      />
    );
  }

  return (
    <PropsCard
      title='Project'
      buttonLabel={noLinkButton ? undefined : 'View Project'}
      onButtonClick={noLinkButton ? undefined : () => navigate(`/project/${propertyProject?.id}`)}
      items={compact([
        { label: 'Name', value: propertyProject?.name },
        propertyProject?.customerMetadata?.projectType
          ? { label: 'Type', value: getProjectTypeLabel(propertyProject.customerMetadata.projectType) }
          : undefined,
        propertyProject?.customerMetadata?.budget ? { label: 'Budget', value: formatUsd(propertyProject?.customerMetadata?.budget) } : undefined,
        propertyProject?.description ? { label: 'Description', value: propertyProject?.description } : undefined,
      ])}
    />
  );
};
